import apiClient from './api-client';
import { getDiscadorApiUrl } from '../config/runtime-api';
import { getToken } from './util.service';

export const UID_ALREADY_IN_ROOM = 'UID_ALREADY_IN_ROOM';

/**
 * Indica se a API recusou a entrada porque o mesmo UID está na sala em outro dispositivo.
 * @param {unknown} error - Erro do axios
 * @returns {boolean}
 */
export const isUidAlreadyInRoomError = (error) =>
  error?.response?.status === 409 &&
  error?.response?.data?.code === UID_ALREADY_IN_ROOM;

/**
 * Renova a reserva deste dispositivo na sala (heartbeat).
 * @param {number} roomId
 * @param {string} uid
 * @param {string} sessionId
 */
export const refreshRoomSession = async (roomId, uid, sessionId) => {
  const response = await apiClient.put(`/salas/${roomId}/sessao`, { uid, sessionId });
  return response.data;
};

/**
 * Libera a reserva deste dispositivo na sala.
 * @param {number} roomId
 * @param {string} uid
 * @param {string} sessionId
 */
export const releaseRoomSession = async (roomId, uid, sessionId) => {
  const response = await apiClient.post(`/salas/${roomId}/sessao/liberar`, { uid, sessionId });
  return response.data;
};

/**
 * Libera a reserva ao fechar/ocultar a página. Usa fetch keepalive porque o axios
 * não garante o envio durante o unload e o sinalizador de keepalive não envia o header Authorization.
 * @param {number} roomId
 * @param {string} uid
 * @param {string} sessionId
 */
export const releaseRoomSessionOnUnload = (roomId, uid, sessionId) => {
  try {
    const baseUrl = String(getDiscadorApiUrl() || '').replace(/\/+$/, '');
    const token = getToken();
    fetch(`${baseUrl}/salas/${roomId}/sessao/liberar`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ uid, sessionId }),
    }).catch(() => {});
  } catch (_error) {}
};

export const updateStatusByUID = async (roomId, uid) => {
  try {
    const response = await apiClient.put(`/salas/${roomId}/uid/${uid}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Cria uma nova sala
 * @param {Object} roomData - Dados da sala
 * @param {number} roomData.idClientes - ID do cliente
 * @param {string} roomData.nome - Nome da sala
 * @param {string} [roomData.uidPrimario] - UID do primário
 * @param {string} [roomData.uidSecundario] - UID do secundário
 * @param {string} [roomData.primarioNaSala] - Status do primário na sala
 * @param {string} [roomData.secundarioNaSala] - Status do secundário na sala
 * @param {number} [roomData.ativo] - Status ativo da sala
 * @returns {Promise<Object>} Dados da sala criada
 */
export const insertRoom = async (roomData) => {
  try {
    const response = await apiClient.post('/salas', roomData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Registra dados de qualidade de conexão da sala
 * @param {Object} networkData - Dados de qualidade de conexão
 * @param {number} networkData.idSalas - ID da sala
 * @param {number} [networkData.uid] - Indica o UID
 * @param {number} [networkData.captureFrameRate] - Taxa de captura de quadros
 * @param {number} [networkData.captureResolutionHeight] - Altura da resolução de captura
 * @param {number} [networkData.captureResolutionWidth] - Largura da resolução de captura
 * @param {string} [networkData.codecType] - Tipo de codec
 * @param {number} [networkData.currentPacketLossRate] - Taxa atual de perda de pacotes
 * @param {number} [networkData.downlinkNetworkQuality] - Qualidade da rede downlink
 * @param {number} [networkData.encodeDelay] - Atraso de codificação
 * @param {number} [networkData.sendBitrate] - Taxa de bits de envio
 * @param {number} [networkData.sendBytes] - Bytes enviados
 * @param {number} [networkData.sendFrameRate] - Taxa de quadros de envio
 * @param {number} [networkData.sendJitterMs] - Jitter de envio em ms
 * @param {number} [networkData.sendPackets] - Pacotes enviados
 * @param {number} [networkData.sendPacketsLost] - Pacotes perdidos no envio
 * @param {number} [networkData.sendResolutionHeight] - Altura da resolução de envio
 * @param {number} [networkData.sendResolutionWidth] - Largura da resolução de envio
 * @param {number} [networkData.sendRttMs] - RTT de envio em ms
 * @param {number} [networkData.targetSendBitrate] - Taxa de bits alvo de envio
 * @param {number} [networkData.totalDuration] - Duração total
 * @param {number} [networkData.totalFreezeTime] - Tempo total de congelamento
 * @param {number} [networkData.uplinkNetworkQuality] - Qualidade da rede uplink
 * @returns {Promise<Object>} Resposta da criação do registro
*/
export const insertNetworkQuality = async (networkData) => {
  try {
    const response = await apiClient.post(
      '/salas/qualidade-conexao',
      networkData
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Atualiza o status do primário na sala
 * @param {number} id - ID da sala
 * @param {string} status - Status do primário ('0' = saiu, '1' = presente)
 * @returns {Promise<Object>} Dados da sala atualizada
*/
export const updatePrimarioStatus = async (id, status) => {
  try {
    const response = await apiClient.put(`/salas/${id}/primario/${status}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Atualiza o status do secundário na sala
 * @param {number} id - ID da sala
 * @param {string} status - Status do secundário ('0' = saiu, '1' = presente)
 * @returns {Promise<Object>} Dados da sala atualizada
 */
export const updateSecundarioStatus = async (id, status) => {
  try {
    const response = await apiClient.put(`/salas/${id}/secundario/${status}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Verifica se a sala pode receber mais participantes
 * @param {string} roomName - Nome da sala
 * @returns {Promise<Object>} Informações sobre capacidade da sala
 */
export const checkRoomCapacity = async (roomName) => {
  try {
    const response = await apiClient.get(`/salas/capacidade/${roomName}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getStatusRoomByUIDs = async (primaryUID, secondaryUID) => {
  try {
    const response = await apiClient.get(`/salas/status-uids/${primaryUID}/${secondaryUID}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const startRoomWaiting = async (roomId) => {
  const response = await apiClient.put(`/salas/${roomId}/espera`);
  return response.data;
};

/**
 * Persiste tempos acumulados da chamada na sala.
 * @param {number} roomId
 * @param {Object} timesData
 * @param {number} timesData.tempoEsperaMs
 * @param {number} timesData.duracaoChamadaMs
 * @param {string|number|null} [timesData.atendimentoIniciadoEm]
 * @param {string|number|null} [timesData.atendimentoFinalizadoEm]
 * @param {boolean} [timesData.secundarioJaPresente]
 * @param {boolean} [timesData.chamadaFinalizada]
 * @returns {Promise<Object>}
 */
export const updateRoomTimes = async (roomId, timesData) => {
  const response = await apiClient.put(`/salas/${roomId}/tempos`, timesData);
  return response.data;
};

/**
 * Normaliza campos de tempo retornados pela API da sala.
 * @param {Object|null|undefined} sala
 * @returns {Object|null}
 */
export const parseRoomTimes = (sala) => {
  if (!sala) return null;

  const tempoEsperaMs = Number(
    sala.tempoEsperaMs ??
      sala.tempo_espera_ms ??
      (Number(sala.tempoEsperaSegundos ?? sala.tempo_espera_segundos) || 0) *
        1000,
  );
  const duracaoChamadaMs = Number(
    sala.duracaoChamadaMs ??
      sala.duracao_chamada_ms ??
      (Number(sala.duracaoChamadaSegundos ?? sala.duracao_chamada_segundos) ||
        0) * 1000,
  );

  return {
    tempoEsperaMs: Number.isFinite(tempoEsperaMs) ? tempoEsperaMs : 0,
    duracaoChamadaMs: Number.isFinite(duracaoChamadaMs) ? duracaoChamadaMs : 0,
    atendimentoIniciadoEm: sala.atendimentoIniciadoEm ?? sala.atendimento_iniciado_em ?? null,
    atendimentoFinalizadoEm: sala.atendimentoFinalizadoEm ?? sala.atendimento_finalizado_em ?? null,
    secundarioJaPresente: Boolean(sala.secundarioJaPresente ?? sala.secundario_ja_presente ?? false),
    chamadaFinalizada: Boolean(sala.chamadaFinalizada ?? sala.chamada_finalizada ?? false),
  };
};
